/**
 * ParticipantTable - the campaign's compact people view.
 *
 * The visible row carries only identity, status, and one overflow affordance.
 * Company, contact, automation position, and timing are disclosed from the row
 * menu in a focused dialog. Participant entry lives in InviteModal.
 *
 * @module components/admin/automation/ParticipantTable
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  IconChevronDown,
  IconDotsVertical,
  IconInfoCircle,
  IconPlayerPause,
  IconPlayerPlay,
  IconTrash,
} from '@tabler/icons-react';
import { ConfirmDialog, DropdownMenu, Modal } from '../../ui';
import { Skeleton } from '../../ui/Skeleton';
import { responseStatusConfig, getResponseDisplayStatus, getResponseDisplayName, getResponseEmail } from '../../../lib/admin-utils';
import {
  listParticipantMessages,
  type AutomationMessageRow,
  type CampaignParticipantRow,
} from '../../../services/automationService';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import type { AutomationBlock } from '../../../lib/api-contracts/automation-blocks';
import type { InterviewData } from '../../../types';
import { blockShortTitle } from './tokens';

const PILL_NEUTRAL = 'bg-muted-foreground text-background';
const PILL_SUCCESS = 'bg-success text-success-foreground';
const PILL_INVITED = 'bg-warning text-white';
const PILL_STARTED = 'bg-warning text-ink';
const PILL_DANGER = 'bg-danger-soft text-ink light:bg-danger light:text-danger-foreground';

/** The funnel filters available in the single compact select. */
type FilterKey = 'all' | 'added' | 'invited' | 'started' | 'completed' | 'delivery_issue';

const FILTERS: Array<{ value: FilterKey; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'added', label: 'Added' },
  { value: 'invited', label: 'Invited' },
  { value: 'started', label: 'Started' },
  { value: 'completed', label: 'Completed' },
  { value: 'delivery_issue', label: 'Delivery issue' },
];

type Row =
  | { kind: 'participant'; key: string; participant: CampaignParticipantRow }
  | { kind: 'direct'; key: string; response: InterviewData };

/** One resolved status pill. */
interface PillValue {
  label: string;
  className: string;
}

/** One label and value in the progressively disclosed details dialog. */
interface DetailItem {
  label: string;
  value: string;
}

/** Details selected from one row menu. */
interface PersonDetails {
  title: string;
  pill: PillValue;
  items: DetailItem[];
  responseId?: string;
  participantId?: string;
}

/** Format one audit timestamp for the viewer's locale. */
function formatAuditTime(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(value);
}

/** Message rows reserve their own sender, timestamp and body while the audit loads. */
function MessageHistory({ messages, loading }: { messages: AutomationMessageRow[]; loading: boolean }) {
  const rows: Array<AutomationMessageRow | undefined> = loading ? [undefined, undefined] : messages;
  return <div role="region" aria-label="Message history" tabIndex={0}
    className="mt-3 max-h-[360px] overflow-y-auto overscroll-contain pr-1 outline-none scrollbar-dark focus-visible:ring-2 focus-visible:ring-volt">
    <ol className="space-y-3">
      {rows.map((message, index) => <li key={message?.id ?? `loading-${index}`} className={`flex ${message?.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
        <article className={`min-w-0 max-w-[88%] break-words rounded-xl px-3 py-2.5 ${message?.direction === 'outbound' ? 'bg-volt/10 text-foreground' : 'bg-surface text-foreground'}${loading ? ' w-full' : ''}`}>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="min-w-0 break-words text-sm font-medium text-foreground">{message ? message.sender_name : <Skeleton width="8rem" />}</span>
            {message ? <time className="text-sm text-muted-foreground" dateTime={message.sent_at}>{formatAuditTime(message.sent_at)}</time>
              : <span className="text-sm text-muted-foreground"><Skeleton width="6rem" /></span>}
          </div>
          {message?.subject && <p className="mt-1 break-words text-sm font-medium text-muted-foreground">{message.subject}</p>}
          <p data-testid="audit-message-body" className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">{message ? message.body : <Skeleton count={2} />}</p>
        </article>
      </li>)}
    </ol>
  </div>;
}

/** Return a short relative time, or an empty value when there is no time. */
function formatRelative(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const difference = then - nowMs;
  const absolute = Math.abs(difference);
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (absolute < 3_600_000) return formatter.format(Math.round(difference / 60_000), 'minute');
  if (absolute < 86_400_000) return formatter.format(Math.round(difference / 3_600_000), 'hour');
  return formatter.format(Math.round(difference / 86_400_000), 'day');
}

/** Resolve durable participant state to the shared badge palette. */
function participantPill(participant: CampaignParticipantRow): PillValue {
  if (participant.unsubscribed_at) return { label: 'Unsubscribed', className: PILL_NEUTRAL };
  if (participant.automation_paused) return { label: 'Paused', className: PILL_NEUTRAL };
  switch (participant.status) {
    case 'added':
      return { label: 'Added', className: PILL_NEUTRAL };
    case 'invited':
      return { label: 'Invited', className: PILL_INVITED };
    case 'started':
      return { label: 'Started', className: PILL_STARTED };
    case 'completed':
      return { label: 'Completed', className: PILL_SUCCESS };
    case 'delivery_issue':
      return { label: 'Delivery issue', className: PILL_DANGER };
    default:
      return { label: participant.status, className: PILL_NEUTRAL };
  }
}

/** A rounded status pill. */
const Pill: React.FC<PillValue> = ({ label, className }) => (
  <span className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-sm font-medium ${className}`}>
    {label}
  </span>
);

/** Props accepted by {@link ParticipantTable}. */
export interface ParticipantTableProps {
  /** Campaign automation participants. */
  participants: CampaignParticipantRow[];
  /** Campaign responses, including direct respondents. */
  responses: InterviewData[];
  /** Open a linked response. */
  onSelectResponse: (responseId: string) => void;
  /** Remove a participant. */
  onDelete: (id: string) => void;
  /** Pause or resume participant automation. */
  onTogglePause: (id: string, paused: boolean) => void;
  /** Current flow blocks, used only in the details dialog. */
  flowBlocks: AutomationBlock[];
  /** Retained for overview layout compatibility. */
  layout?: 'full' | 'compact';
  /** Whether participant mutations are available. */
  canManage?: boolean;
  /** Message loader override used by tests and alternate data surfaces. */
  loadMessages?: (participantId: string) => Promise<AutomationMessageRow[]>;
}

/** Render the merged, deliberately compact people list. */
export const ParticipantTable: React.FC<ParticipantTableProps> = ({
  participants,
  responses,
  onSelectResponse,
  onDelete,
  onTogglePause,
  flowBlocks,
  layout = 'full',
  canManage = true,
  loadMessages = listParticipantMessages,
}) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [details, setDetails] = useState<PersonDetails | null>(null);
  const [messages, setMessages] = useState<AutomationMessageRow[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(false);
  const [messagesAttempt, setMessagesAttempt] = useState(0);

  useEffect(() => {
    const participantId = details?.participantId;
    let cancelled = false;
    setMessages([]);
    setMessagesError(false);
    if (!participantId) {
      setMessagesLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setMessagesLoading(true);
    void loadMessages(participantId)
      .then((loadedMessages) => {
        if (!cancelled) setMessages(loadedMessages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        captureException(error instanceof Error ? error : String(error), 'ParticipantTable.loadMessages', {
          participantId,
        });
        setMessagesError(true);
      })
      .finally(() => {
        if (!cancelled) setMessagesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [details?.participantId, loadMessages, messagesAttempt]);

  const blockPositionById = useMemo(() => {
    const map = new Map<string, { block: AutomationBlock; number: number }>();
    for (const [index, block] of flowBlocks.entries()) {
      map.set(block.id, { block, number: index + 1 });
    }
    return map;
  }, [flowBlocks]);

  const rows = useMemo<Row[]>(() => {
    const linkedResponseIds = new Set(
      participants
        .map((participant) => participant.interview_data_id)
        .filter((id): id is string => Boolean(id)),
    );
    return [
      ...participants.map((participant): Row => ({
        kind: 'participant',
        key: `p-${participant.id}`,
        participant,
      })),
      ...responses
        .filter((response) => !linkedResponseIds.has(response.id))
        .map((response): Row => ({ kind: 'direct', key: `r-${response.id}`, response })),
    ];
  }, [participants, responses]);

  const visibleRows = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((row) => {
      if (row.kind === 'participant') {
        if (row.participant.unsubscribed_at || row.participant.automation_paused) return false;
        return row.participant.status === filter;
      }
      const status = getResponseDisplayStatus(row.response);
      if (filter === 'started') return status === 'pending' || status === 'in_progress';
      if (filter === 'completed') return status === 'completed';
      return false;
    });
  }, [filter, rows]);

  const participantDetails = (participant: CampaignParticipantRow): PersonDetails => {
    const pill = participantPill(participant);
    const stepPosition = participant.current_block_id
      ? blockPositionById.get(participant.current_block_id)
      : undefined;
    const nextTime = formatRelative(participant.next_action_at);
    let nextStep = 'Completed';
    if (stepPosition && participant.automation_paused) {
      nextStep = `${stepPosition.number}. ${blockShortTitle(stepPosition.block)} · Paused`;
    } else if (stepPosition && participant.status === 'delivery_issue') {
      nextStep = `${stepPosition.number}. ${blockShortTitle(stepPosition.block)} · Delivery issue`;
    } else if (stepPosition && nextTime) {
      nextStep = `${stepPosition.number}. ${blockShortTitle(stepPosition.block)} · ${nextTime}`;
    } else if (nextTime) {
      nextStep = `Scheduled · ${nextTime}`;
    }
    const items: DetailItem[] = [
      { label: 'Email', value: participant.email },
      ...(participant.company ? [{ label: 'Company', value: participant.company }] : []),
      ...(participant.phone ? [{ label: 'Phone', value: participant.phone }] : []),
      ...(participant.timezone ? [{ label: 'Timezone', value: participant.timezone }] : []),
      { label: 'Next step', value: nextStep },
    ];
    return {
      title: participant.name || participant.email,
      pill,
      items,
      participantId: participant.id,
      ...(participant.interview_data_id ? { responseId: participant.interview_data_id } : {}),
    };
  };

  const directDetails = (response: InterviewData): PersonDetails => {
    const status = getResponseDisplayStatus(response);
    const config = responseStatusConfig[status];
    const email = getResponseEmail(response);
    return {
      title: getResponseDisplayName(response),
      pill: { label: config.label, className: config.className },
      items: [
        ...(email ? [{ label: 'Email', value: email }] : []),
        ...(response.interviewee_company ? [{ label: 'Company', value: response.interviewee_company }] : []),
        { label: 'Source', value: 'Direct' },
      ],
      responseId: response.id,
    };
  };

  return (
    <>
      <div data-layout={layout} className={`overflow-hidden rounded-2xl border border-border bg-background ${layout === 'compact' ? 'min-h-0 flex flex-col' : ''}`}>
        <div className="shrink-0 flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 className="text-sm font-medium leading-[1.4] text-foreground">Participants ({rows.length})</h2>
          <div className="relative">
            <select
              aria-label="Filter participants"
              value={filter}
              onChange={(event) => setFilter(event.target.value as FilterKey)}
              className="min-h-11 touch-manipulation appearance-none rounded-lg bg-surface py-1.5 pl-3 pr-8 text-sm text-muted-foreground outline-none transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
            >
              {FILTERS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <IconChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </div>

        {visibleRows.length === 0 ? (
          <p className="px-4 py-8 text-sm text-muted-foreground">Nothing here yet</p>
        ) : (
          <div
            role="list"
            aria-label="Campaign participants"
            className="min-h-0 max-h-[560px] divide-y divide-border/50 overflow-y-auto overscroll-contain scrollbar-dark"
          >
            {visibleRows.map((row) => row.kind === 'participant' ? (
              <ParticipantRow
                key={row.key}
                participant={row.participant}
                canManage={canManage}
                onSelectResponse={onSelectResponse}
                onViewDetails={() => setDetails(participantDetails(row.participant))}
                onDelete={onDelete}
                onTogglePause={onTogglePause}
              />
            ) : (
              <DirectRow
                key={row.key}
                response={row.response}
                onSelectResponse={onSelectResponse}
                onViewDetails={() => setDetails(directDetails(row.response))}
              />
            ))}
          </div>
        )}
      </div>

      <Modal
        open={details !== null}
        onClose={() => setDetails(null)}
        title={details?.title}
        maxWidth={details?.participantId ? 'max-w-2xl' : 'max-w-sm'}
        heightClass={details?.participantId ? 'max-h-[calc(100vh-3rem)] overflow-y-auto' : undefined}
      >
        {details && (
          <div className="space-y-4">
            <Pill label={details.pill.label} className={details.pill.className} />
            {details.items.length > 0 && (
              <dl className="divide-y divide-border/60">
                {details.items.map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-6 py-3 first:pt-0 last:pb-0">
                    <dt className="text-sm text-muted-foreground">{item.label}</dt>
                    <dd className="min-w-0 break-words text-right text-sm text-foreground">{item.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {details.responseId && (
              <button
                type="button"
                onClick={() => {
                  onSelectResponse(details.responseId as string);
                  setDetails(null);
                }}
                className="min-h-11 w-full touch-manipulation cursor-pointer rounded-lg bg-surface px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
              >
                Open conversation
              </button>
            )}
            {details.participantId && (
              <section
                className="border-t border-border/60 pt-4"
                aria-busy={messagesLoading}
              >
                <h3 className="text-sm font-medium text-foreground">Messages</h3>
                <p className="sr-only" aria-live="polite">
                  {messagesLoading
                    ? 'Loading messages'
                    : messagesError
                      ? 'Messages unavailable'
                      : `${messages.length} ${messages.length === 1 ? 'message' : 'messages'} loaded`}
                </p>
                {messagesError ? (
                  <div className="flex items-center justify-between gap-3 py-4">
                    <p className="text-sm text-muted-foreground">Messages unavailable</p>
                    <button
                      type="button"
                      onClick={() => setMessagesAttempt((attempt) => attempt + 1)}
                      className="min-h-11 touch-manipulation cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
                    >
                      Retry
                    </button>
                  </div>
                ) : !messagesLoading && messages.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">No messages yet</p>
                ) : (
                  <div role={messagesLoading ? 'status' : undefined} aria-label={messagesLoading ? 'Loading messages' : undefined}>
                    <MessageHistory messages={messages} loading={messagesLoading} />
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

/** Compact identity cell shared by participant and direct rows. */
const PersonCell: React.FC<{
  name: string;
  email: string;
  onClick?: () => void;
}> = ({ name, email, onClick }) => {
  const content = (
    <>
      <span className="truncate text-sm font-medium text-foreground">{name}</span>
      {email && email !== name && <span className="truncate text-sm text-muted-foreground">{email}</span>}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="flex min-h-11 min-w-0 touch-manipulation cursor-pointer flex-col items-start justify-center text-left focus-visible:ring-2 focus-visible:ring-volt">
      {content}
    </button>
  ) : (
    <div className="flex min-w-0 flex-col">{content}</div>
  );
};

/** One participant row. */
const ParticipantRow: React.FC<{
  participant: CampaignParticipantRow;
  canManage: boolean;
  onSelectResponse: (id: string) => void;
  onViewDetails: () => void;
  onDelete: (id: string) => void;
  onTogglePause: (id: string, paused: boolean) => void;
}> = ({ participant, canManage, onSelectResponse, onViewDetails, onDelete, onTogglePause }) => {
  const actionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const pill = participantPill(participant);
  const responseId = participant.interview_data_id;
  const menuItems = [
    {
      label: 'View details',
      icon: <IconInfoCircle className="h-4 w-4" />,
      onClick: onViewDetails,
    },
    ...(canManage ? [
      {
        label: participant.automation_paused ? 'Resume' : 'Pause',
        icon: participant.automation_paused
          ? <IconPlayerPlay className="h-4 w-4" />
          : <IconPlayerPause className="h-4 w-4" />,
        onClick: () => onTogglePause(participant.id, !participant.automation_paused),
      },
      {
        label: 'Remove',
        icon: <IconTrash className="h-4 w-4" />,
        className: 'text-danger',
        onClick: () => setConfirmRemove(true),
      },
    ] : []),
  ];

  const participantName = participant.name || participant.email;

  return (
    <>
      <div role="listitem" className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-hover/60 motion-reduce:transition-none">
        <div className="min-w-0 flex-1">
          <PersonCell
            name={participant.name || 'Unknown'}
            email={participant.email}
            onClick={responseId ? () => onSelectResponse(responseId) : undefined}
          />
        </div>
        <Pill label={pill.label} className={pill.className} />
        <DropdownMenu
          trigger={(
            <button
              ref={actionsButtonRef}
              type="button"
              aria-label="Person actions"
              className="flex h-11 w-11 touch-manipulation cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
            >
              <IconDotsVertical aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          items={menuItems}
          anchor="bottom end"
          width="w-40"
        />
      </div>
      <ConfirmDialog
        open={confirmRemove}
        onClose={() => {
          setConfirmRemove(false);
          window.setTimeout(() => actionsButtonRef.current?.focus(), 0);
        }}
        onConfirm={() => {
          setConfirmRemove(false);
          onDelete(participant.id);
        }}
        title={`Remove ${participantName}?`}
        description="This permanently removes their automation and message history."
        confirmLabel="Remove person"
      />
    </>
  );
};

/** One direct respondent row. */
const DirectRow: React.FC<{
  response: InterviewData;
  onSelectResponse: (id: string) => void;
  onViewDetails: () => void;
}> = ({ response, onSelectResponse, onViewDetails }) => {
  const status = getResponseDisplayStatus(response);
  const config = responseStatusConfig[status];
  return (
    <div role="listitem" className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-hover/60 motion-reduce:transition-none">
      <div className="min-w-0 flex-1">
        <PersonCell
          name={getResponseDisplayName(response)}
          email={getResponseEmail(response) || ''}
          onClick={() => onSelectResponse(response.id)}
        />
      </div>
      <Pill label={config.label} className={config.className} />
      <DropdownMenu
        trigger={(
          <button
            type="button"
            aria-label="Person actions"
            className="flex h-11 w-11 touch-manipulation cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-surface-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-volt motion-reduce:transition-none"
          >
            <IconDotsVertical aria-hidden="true" className="h-4 w-4" />
          </button>
        )}
        items={[{
          label: 'View details',
          icon: <IconInfoCircle className="h-4 w-4" />,
          onClick: onViewDetails,
        }]}
        anchor="bottom end"
        width="w-40"
      />
    </div>
  );
};
